import { useDataStore, useFilterStore } from "@/stores";
import clsx from "clsx";
import { forwardRef, useEffect, useImperativeHandle } from "react";

interface SoftwareFilterProps {
    ifc?: boolean;
    shouldConfirm?: boolean;
    small?: boolean;
}

export interface SoftwareFilterRef {
    confirmChange: () => void;
}

const SoftwareFilter = forwardRef<SoftwareFilterRef, SoftwareFilterProps>(({ ifc, shouldConfirm, small }, ref) => {
    const software = useDataStore((state) => state.software);
    const activeVersion = useFilterStore((state) => state.activeModalVersion);
    const activeSoftware = useFilterStore((state) => state.activeSoftware);
    const activeIfcVersion = useFilterStore((state) => state.activeIfcVersion);
    const setActiveSoftware = useFilterStore((state) => state.setActiveSoftware);
    const setActiveIfcVersion = useFilterStore((state) => state.setActiveIfcVersion);

    const unconfirmedIfcVersion = useFilterStore((state) => state.unconfirmedIfcVersion);
    const setUnconfirmedIfcVersion = useFilterStore((state) => state.setUnconfirmedIfcVersion);
    const unconfirmedSoftware = useFilterStore((state) => state.unconfirmedSoftware);
    const setUnconfirmedSoftware = useFilterStore((state) => state.setUnconfirmedSoftware);

    const activeSoftwareList = ifc ? activeIfcVersion : activeSoftware;

    useImperativeHandle(ref, () => ({
        confirmChange: () => {
            if (ifc) setActiveIfcVersion(unconfirmedIfcVersion);
            else setActiveSoftware(unconfirmedSoftware);
        },
    }));

    useEffect(() => {
        if (activeVersion && software[activeVersion]) {
            const activeSoftwareList = software[activeVersion].filter((x) => x.active && !x.isIfc);

            for (const item of activeSoftwareList) {
                document.documentElement.style.removeProperty(`--color-software-${item.id}`);
                document.documentElement.style.setProperty(`--color-software-${item.id}`, item.color ?? "#05b692");

                let softwareStyle = document.getElementById(`style-software-${item.id}`);
                if (softwareStyle) document.getElementsByTagName("head")[0].removeChild(softwareStyle);

                softwareStyle = document.createElement("style");
                softwareStyle.id = `style-software-${item.id}`;
                softwareStyle.innerHTML = `
                    .software-${item.id} {
                        background-color: color-mix(in oklab, var(--color-software-${item.id}) 10%, transparent);
                        color: var(--color-software-${item.id});
                        &:is(:where(.group):has(*:checked) *) {
                            background-color: var(--color-software-${item.id});
                            color: white;
                        }
                    }

                    .software-${item.id}:hover {
                        background-color: color-mix(in oklab, var(--color-software-${item.id}) 90%, transparent);
                        color: white;
                    }

                    .software-${item.id}-selector {
                        border-color: rgba(0, 0, 0, 0.25);
                    }

                    .software-${item.id}-selector:hover {
                        border-color: rgba(0, 0, 0, 0.25);
                    }

                    .software-${item.id}-selected {
                        background-color: var(--color-software-${item.id});
                    }

                    ${
                        item.invert &&
                        `.software-${item.id} img {
                        &:is(:where(.group):has(*:checked) *) {
                            filter: invert(1);
                        }
                    }
                    .software-${item.id}:hover img {
                        filter: invert(1);
                    }`
                    }
                `;
                document.getElementsByTagName("head")[0].appendChild(softwareStyle);
            }
        }
    }, [software, activeVersion]);

    if (!software[activeVersion!]) return null;

    return software[activeVersion!]
        .filter((x) => {
            return !x.active || ifc ? x.isIfc : !x.isIfc;
        })
        .sort((a, b) => a.order - b.order)
        .map((software) => {
            const id = `${ifc ? "ifc" : "software"}-${shouldConfirm ? "confirm" : "direct"}-${software.id}`;
            const isActive = activeSoftwareList === software.id;
            const isUnconfirmed = ifc ? unconfirmedIfcVersion === software.id : unconfirmedSoftware === software.id;
            const isSelected = shouldConfirm ? isUnconfirmed : isActive;

            const handleClick = () => {
                if (shouldConfirm) {
                    if (ifc) setUnconfirmedIfcVersion(software.id);
                    else setUnconfirmedSoftware(unconfirmedSoftware === software.id ? null : software.id);
                } else {
                    if (ifc) setActiveIfcVersion(software.id);
                    else setActiveSoftware(activeSoftware === software.id ? null : software.id);
                }
            };

            return (
                <div key={software.id} className="group">
                    <div
                        className={clsx([
                            "flex flex-wrap rounded-md text-base font-medium items-center cursor-pointer",
                            small ? "min-h-14 gap-3 px-5 py-3" : "min-h-[90px] gap-3 p-5",
                            // "hover:bg-primary-600 hover:text-white",
                            // "group-has-checked:bg-primary-500 group-has-checked:text-white",
                            ifc
                                ? "bg-primary-100 text-primary-500 hover:bg-primary-600 group-has-checked:bg-primary-500"
                                : `software-${software.id}`,
                            "hover:text-white group-has-checked:text-white",
                        ])}
                        onClick={handleClick}
                    >
                        <div className="grid size-6 grid-cols-1 shrink-0">
                            <input
                                id={id}
                                name={id}
                                value={software.id}
                                type="radio"
                                checked={isSelected}
                                onChange={() => {}}
                                className={clsx([
                                    " cursor-pointer col-start-1 row-start-1 appearance-none rounded-full border-2 min-w-6 min-h-6",
                                    "checked:outline-2 checked:-outline-offset-4",
                                    ifc
                                        ? "border-primary-300 bg-primary-100 checked:outline-primary-500"
                                        : `border-black/25 bg-transparent group-hover:border-white/25 checked:border-white/25 checked:outline-transparent`,
                                ])}
                            />
                            <div className="pointer-events-none opacity-0 group-has-checked:opacity-100 bg-white w-4 h-4 rounded-full col-start-1 row-start-1 self-center justify-self-center" />
                            <div
                                className={clsx([
                                    "pointer-events-none opacity-0 group-has-checked:opacity-100  w-3 h-3 rounded-full col-start-1 row-start-1 self-center justify-self-center",
                                    ifc ? "bg-primary-500" : `software-${software.id}-selected`,
                                ])}
                            />
                        </div>
                        {!ifc && software.icon && (
                            <div className="shrink-0">
                                <img
                                    src={software.icon}
                                    alt={software.name}
                                    className={clsx([small ? "h-[30px]" : "h-[50px]", "w-auto"])}
                                />
                            </div>
                        )}
                        {software.name}
                    </div>
                </div>
            );
        });
});

export default SoftwareFilter;
